import { pointFrom, type LocalPoint } from "@excalidraw/math";

import type { AppState } from "@excalidraw/excalidraw/types";

import {
  getAllColorsSpecificShade,
  ROUNDNESS,
} from "@excalidraw/common";

import { bindBindingElement } from "./binding";
import { LinearElementEditor } from "./linearElementEditor";
import { newArrowElement, newElement } from "./newElement";
import { isArrowElement } from "./typeChecks";
import type {
  ElementsMap,
  ExcalidrawArrowElement,
  ExcalidrawBindableElement,
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "./types";

import type { Scene } from "./Scene";

const HORIZONTAL_OFFSET = 100;
const VERTICAL_OFFSET = 60;
const NODE_WIDTH = 500;
const NODE_HEIGHT = 40;
const ROOT_NODE_WIDTH = 150;
const ROOT_NODE_HEIGHT = 50;
const PADDING = 6;

const MINDMAP_FONT_SIZES = [28, 22, 18, 16]; // root, level 1, level 2, level 3+

export const isMindmapNode = (
  element: ExcalidrawElement,
): boolean => {
  return !!element.customData?.isMindmapNode;
};

// ---------------------------------------------------------------------------
// Nested mindmap containers
// A node may be visually nested inside another mindmap node (the "container
// parent"). Nested nodes follow the container: move/resize/delete together,
// similar to how text elements behave inside a container (containerId).
// The relation is stored in customData.containerParentId.
// ---------------------------------------------------------------------------

export interface ContainerOffset {
  /** Offset of nested node from container's top-left, as fractions (0..1). */
  dxFrac: number;
  dyFrac: number;
  /** Nested node size relative to container size (0..1). */
  widthFrac: number;
  heightFrac: number;
}

export const isNestedInsideContainer = (node: ExcalidrawElement): boolean => {
  return !!node.customData?.containerParentId;
};

/**
 * Given a selection of mindmap nodes, detect which one is the visual
 * container — i.e. whose bounding box contains the CENTER of every other
 * selected node. If multiple candidates qualify, returns the one with the
 * largest area (outermost). Returns null when the selection can't be
 * interpreted as a nesting operation (mixed types, no geometric containment).
 */
export const detectMindmapContainer = (
  selected: readonly ExcalidrawElement[],
): ExcalidrawElement | null => {
  if (selected.length < 2 || !selected.every(isMindmapNode)) {
    return null;
  }
  const candidates = selected.filter((candidate) => {
    return selected.every((other) => {
      if (other.id === candidate.id) {
        return true;
      }
      const cx = other.x + other.width / 2;
      const cy = other.y + other.height / 2;
      return (
        cx >= candidate.x &&
        cx <= candidate.x + candidate.width &&
        cy >= candidate.y &&
        cy <= candidate.y + candidate.height
      );
    });
  });
  if (candidates.length === 0) {
    return null;
  }
  return candidates.reduce((biggest, c) =>
    c.width * c.height > biggest.width * biggest.height ? c : biggest,
  );
};

export const getContainerParent = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement | null => {
  const id = node.customData?.containerParentId as string | undefined;
  if (!id) {
    return null;
  }
  const parent = elementsMap.get(id);
  return parent && !parent.isDeleted ? parent : null;
};

/**
 * All nodes directly nested inside the given container (one level).
 * Does NOT traverse logical tree descendants — use getConnectedDescendants
 * separately to gather those.
 */
export const getNestedChildren = (
  container: ExcalidrawElement,
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  const result: ExcalidrawElement[] = [];
  for (const el of elements) {
    if (el.isDeleted) {
      continue;
    }
    if (el.customData?.containerParentId === container.id) {
      result.push(el);
    }
  }
  return result;
};

const computeContainerOffset = (
  container: ExcalidrawElement,
  node: ExcalidrawElement,
): ContainerOffset => {
  const cw = container.width || 1;
  const ch = container.height || 1;
  return {
    dxFrac: (node.x - container.x) / cw,
    dyFrac: (node.y - container.y) / ch,
    widthFrac: node.width / cw,
    heightFrac: node.height / ch,
  };
};

/**
 * Mark `node` as nested inside `container`: sets containerParentId and
 * captures the current relative offset/size so future moves and resizes
 * of the container can reapply the same layout.
 */
export const nestMindmapNode = (
  node: ExcalidrawElement,
  container: ExcalidrawElement,
  scene: Scene,
): void => {
  if (node.id === container.id) {
    return;
  }
  const offset = computeContainerOffset(container, node);
  scene.mutateElement(node, {
    customData: {
      ...node.customData,
      containerParentId: container.id,
      containerOffset: offset,
    },
  });

  // Ensure z-order: nested node must render above its container.
  const all = scene.getElementsIncludingDeleted();
  const nestedIdx = all.findIndex((el) => el.id === node.id);
  const containerIdx = all.findIndex((el) => el.id === container.id);
  if (nestedIdx !== -1 && containerIdx !== -1 && nestedIdx < containerIdx) {
    const reordered = [...all];
    const [moved] = reordered.splice(nestedIdx, 1);
    // Re-find container index after removal (may have shifted)
    const newContainerIdx = reordered.findIndex((el) => el.id === container.id);
    reordered.splice(newContainerIdx + 1, 0, moved);
    scene.replaceAllElements(reordered);
  }
};

export const unnestMindmapNode = (
  node: ExcalidrawElement,
  scene: Scene,
): void => {
  if (!isNestedInsideContainer(node)) {
    return;
  }
  const { containerParentId, containerOffset, ...rest } = node.customData ?? {};
  scene.mutateElement(node, { customData: rest });
};

/**
 * Returns the set of all element IDs that should be deleted together with
 * a mindmap container: the container itself, its nested children, and the
 * logical descendants (+ binding arrows) of each nested child.
 */
export const getMindmapContainerDeletionIds = (
  container: ExcalidrawElement,
  elements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
): Set<string> => {
  const ids = new Set<string>([container.id]);
  for (const nested of getNestedChildren(container, elements)) {
    ids.add(nested.id);
    const descendants = getConnectedDescendants(nested, elementsMap);
    for (const id of descendants) {
      ids.add(id);
    }
  }
  return ids;
};

/**
 * Apply a translation (dx, dy) to all elements nested inside `container`
 * and their logical descendants. Binding arrows between them are updated
 * separately by the caller via updateBoundElements.
 */
export const translateNestedChildren = (
  container: ExcalidrawElement,
  dx: number,
  dy: number,
  elements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
  scene: Scene,
): void => {
  for (const nested of getNestedChildren(container, elements)) {
    scene.mutateElement(nested, {
      x: nested.x + dx,
      y: nested.y + dy,
    });
    const descendants = getConnectedDescendants(nested, elementsMap);
    for (const id of descendants) {
      const el = elementsMap.get(id);
      if (!el || el.isDeleted) {
        continue;
      }
      scene.mutateElement(el, { x: el.x + dx, y: el.y + dy });
    }
  }
};

/**
 * Reapply container-relative offsets after the container was moved or
 * resized. Each nested child is repositioned/resized using its stored
 * fractional offset, and its logical descendants are translated by the
 * same delta so the whole subtree follows the container.
 *
 * @param resize — when true, also updates width/height of nested (resize
 * path). When false, only x/y are updated (drag path).
 */
export const applyContainerLayoutToNested = (
  container: ExcalidrawElement,
  elements: readonly ExcalidrawElement[],
  elementsMap: ElementsMap,
  scene: Scene,
  options?: { resize?: boolean },
): void => {
  const resize = !!options?.resize;
  for (const nested of getNestedChildren(container, elements)) {
    const offset = nested.customData?.containerOffset as
      | ContainerOffset
      | undefined;
    if (!offset) {
      continue;
    }
    const newX = container.x + offset.dxFrac * container.width;
    const newY = container.y + offset.dyFrac * container.height;
    const dx = newX - nested.x;
    const dy = newY - nested.y;

    const update: Partial<ExcalidrawElement> = { x: newX, y: newY };
    if (resize) {
      (update as any).width = offset.widthFrac * container.width;
      (update as any).height = offset.heightFrac * container.height;
    }
    scene.mutateElement(nested, update);

    // Translate logical descendants by the same delta (keeps subtree intact)
    if (dx !== 0 || dy !== 0) {
      const descendants = getConnectedDescendants(nested, elementsMap);
      for (const id of descendants) {
        const el = elementsMap.get(id);
        if (!el || el.isDeleted) {
          continue;
        }
        scene.mutateElement(el, { x: el.x + dx, y: el.y + dy });
      }
    }
  }
};

/**
 * Get all child nodes of a mind map node by following outgoing arrows
 * (arrows where startBinding points to parentNode).
 */
export const getMindmapChildren = (
  parentNode: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  const children: ExcalidrawElement[] = [];

  if (!parentNode.boundElements) {
    return children;
  }

  for (const bound of parentNode.boundElements) {
    if (bound.type !== "arrow") {
      continue;
    }
    const arrow = elementsMap.get(bound.id);
    if (!arrow || !isArrowElement(arrow)) {
      continue;
    }
    if (
      arrow.startBinding &&
      arrow.startBinding.elementId === parentNode.id &&
      arrow.endBinding
    ) {
      const child = elementsMap.get(arrow.endBinding.elementId);
      if (child && isMindmapNode(child)) {
        children.push(child);
      }
    }
  }

  return children;
};

/**
 * Get ALL child elements connected via outgoing arrows (any type, not just mindmap nodes).
 * Used for collapse/expand to hide everything connected.
 */
export const getConnectedChildren = (
  parentNode: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement[] => {
  const children: ExcalidrawElement[] = [];

  if (!parentNode.boundElements) {
    return children;
  }

  for (const bound of parentNode.boundElements) {
    if (bound.type !== "arrow") {
      continue;
    }
    const arrow = elementsMap.get(bound.id);
    if (!arrow || !isArrowElement(arrow)) {
      continue;
    }
    if (
      arrow.startBinding &&
      arrow.startBinding.elementId === parentNode.id &&
      arrow.endBinding
    ) {
      const child = elementsMap.get(arrow.endBinding.elementId);
      if (child && !child.isDeleted) {
        children.push(child);
      }
    }
  }

  return children;
};

/**
 * Recursively collect all connected descendant IDs and their arrows.
 * Unlike getMindmapDescendants, this follows ANY connected element.
 */
export const getConnectedDescendants = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): Set<string> => {
  const ids = new Set<string>();

  const traverse = (current: ExcalidrawElement) => {
    if (!current.boundElements) {
      return;
    }
    for (const bound of current.boundElements) {
      if (bound.type !== "arrow") {
        continue;
      }
      const arrow = elementsMap.get(bound.id);
      if (!arrow || !isArrowElement(arrow)) {
        continue;
      }
      if (
        arrow.startBinding &&
        arrow.startBinding.elementId === current.id &&
        arrow.endBinding
      ) {
        const child = elementsMap.get(arrow.endBinding.elementId);
        if (child && !child.isDeleted && !ids.has(child.id)) {
          ids.add(arrow.id);
          ids.add(child.id);
          traverse(child);
        }
      }
    }
  };

  traverse(node);
  return ids;
};

/**
 * Find the parent node of a mind map node by following incoming arrows
 * (arrows where endBinding points to node).
 */
export const getMindmapParent = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement | null => {
  if (!node.boundElements) {
    return null;
  }

  for (const bound of node.boundElements) {
    if (bound.type !== "arrow") {
      continue;
    }
    const arrow = elementsMap.get(bound.id);
    if (!arrow || !isArrowElement(arrow)) {
      continue;
    }
    if (
      arrow.endBinding &&
      arrow.endBinding.elementId === node.id &&
      arrow.startBinding
    ) {
      const parent = elementsMap.get(arrow.startBinding.elementId);
      if (parent && isMindmapNode(parent)) {
        return parent;
      }
    }
  }

  return null;
};

/**
 * Check if a mind map node is collapsed.
 */
export const isMindmapNodeCollapsed = (
  element: ExcalidrawElement,
): boolean => {
  return !!element.customData?.isCollapsed;
};

/**
 * Find the parent of any element by following incoming arrows.
 * Returns the source element of the first incoming arrow found.
 */
export const getConnectedParent = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement | null => {
  if (!node.boundElements) {
    return null;
  }

  for (const bound of node.boundElements) {
    if (bound.type !== "arrow") {
      continue;
    }
    const arrow = elementsMap.get(bound.id);
    if (!arrow || !isArrowElement(arrow)) {
      continue;
    }
    if (
      arrow.endBinding &&
      arrow.endBinding.elementId === node.id &&
      arrow.startBinding
    ) {
      const parent = elementsMap.get(arrow.startBinding.elementId);
      if (parent && !parent.isDeleted) {
        return parent;
      }
    }
  }

  return null;
};

/**
 * Check if an element should be hidden because any ancestor is collapsed.
 * Works with ANY element connected via arrows, not just mindmap nodes.
 */
export const isMindmapElementHidden = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
): boolean => {
  // Um node é escondido se ALGUM ancestral (subindo pela árvore lógica
  // via binding arrow OU pela cadeia de containerParentId do nesting
  // visual) estiver colapsado. Precisamos traversar as DUAS dimensões
  // em paralelo porque um node pode ser descendente lógico por uma rota
  // e descendente visual por outra — se qualquer ancestral está fechado,
  // ele some.
  const visited = new Set<string>();

  const check = (node: ExcalidrawElement): boolean => {
    if (visited.has(node.id)) {
      return false;
    }
    visited.add(node.id);

    const arrowParent = getConnectedParent(node, elementsMap);
    if (arrowParent) {
      if (isMindmapNodeCollapsed(arrowParent)) {
        return true;
      }
      if (check(arrowParent)) {
        return true;
      }
    }

    const containerParent = getContainerParent(node, elementsMap);
    if (containerParent) {
      if (isMindmapNodeCollapsed(containerParent)) {
        return true;
      }
      if (check(containerParent)) {
        return true;
      }
    }

    return false;
  };

  return check(element);
};

/**
 * Check if an arrow should be hidden (connects to/from a hidden node).
 */
export const isMindmapArrowHidden = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
): boolean => {
  if (!isArrowElement(element)) {
    return false;
  }
  const arrow = element as ExcalidrawArrowElement;
  if (arrow.endBinding) {
    const target = elementsMap.get(arrow.endBinding.elementId);
    if (target && isMindmapElementHidden(target, elementsMap)) {
      return true;
    }
  }
  if (arrow.startBinding) {
    const source = elementsMap.get(arrow.startBinding.elementId);
    if (source && isMindmapElementHidden(source, elementsMap)) {
      return true;
    }
  }
  return false;
};

/**
 * Toggle collapse state of a mind map node.
 */
export const toggleMindmapCollapse = (
  element: ExcalidrawElement,
  scene: Scene,
): void => {
  const isCollapsed = !element.customData?.isCollapsed;
  scene.mutateElement(element, {
    customData: { ...element.customData, isCollapsed },
  });
};

/**
 * Recursively collect all descendant node IDs and connecting arrow IDs.
 */
export const getMindmapDescendants = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): Set<string> => {
  const ids = new Set<string>();

  const traverse = (current: ExcalidrawElement) => {
    if (!current.boundElements) {
      return;
    }

    for (const bound of current.boundElements) {
      if (bound.type !== "arrow") {
        continue;
      }
      const arrow = elementsMap.get(bound.id);
      if (!arrow || !isArrowElement(arrow)) {
        continue;
      }
      if (
        arrow.startBinding &&
        arrow.startBinding.elementId === current.id &&
        arrow.endBinding
      ) {
        const child = elementsMap.get(arrow.endBinding.elementId);
        if (child && isMindmapNode(child) && !ids.has(child.id)) {
          ids.add(arrow.id);
          ids.add(child.id);
          traverse(child);
        }
      }
    }
  };

  traverse(node);
  return ids;
};

/**
 * Create a non-elbowed binding arrow between two mind map nodes.
 */
const createMindmapBindingArrow = (
  startElement: ExcalidrawBindableElement,
  endElement: ExcalidrawBindableElement,
  direction: "right" | "down",
  scene: Scene,
  appState: AppState,
  colorOverride?: string,
) => {
  let startX: number;
  let startY: number;
  let endX: number;
  let endY: number;

  if (direction === "right") {
    // Arrow: right edge of parent → left edge of child
    startX = startElement.x + startElement.width + PADDING;
    startY = startElement.y + startElement.height / 2;
    endX = endElement.x - startX - PADDING;
    endY = endElement.y + endElement.height / 2 - startY;
  } else {
    // Arrow: bottom edge of parent → top edge of child
    startX = startElement.x + startElement.width / 2;
    startY = startElement.y + startElement.height + PADDING;
    endX = endElement.x + endElement.width / 2 - startX;
    endY = endElement.y - startY - PADDING;
  }

  const bindingArrow = newArrowElement({
    type: "arrow",
    x: startX,
    y: startY,
    startArrowhead: null,
    endArrowhead: null,
    strokeColor: colorOverride ?? startElement.strokeColor,
    strokeStyle: startElement.strokeStyle,
    strokeWidth: startElement.strokeWidth,
    opacity: startElement.opacity,
    roughness: startElement.roughness,
    roundness: { type: ROUNDNESS.PROPORTIONAL_RADIUS },
    points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(endX, endY)],
    elbowed: false,
  });

  bindBindingElement(
    bindingArrow,
    startElement,
    "orbit",
    "start",
    scene,
  );
  bindBindingElement(bindingArrow, endElement, "orbit", "end", scene);

  // Recalculate arrow points after binding (same pattern as flowchart.ts:472-483)
  LinearElementEditor.movePoints(
    bindingArrow,
    scene,
    new Map([
      [1, { point: bindingArrow.points[1] }],
    ]),
  );

  return bindingArrow;
};

/**
 * Create a child node to the right of the parent node.
 */
export const addMindmapChild = (
  parentNode: ExcalidrawBindableElement,
  appState: AppState,
  scene: Scene,
): { newNode: OrderedExcalidrawElement; bindingArrow: OrderedExcalidrawElement } => {
  const elementsMap = scene.getNonDeletedElementsMap();

  // Scale new node dimensions and spacing by zoom so a node created at 200%
  // zoom looks the same visual size as one created at 100%.
  const zoom = appState.zoom?.value || 1;
  const childWidth = NODE_WIDTH / zoom;
  const childHeight = NODE_HEIGHT / zoom;
  const hOffset = HORIZONTAL_OFFSET / zoom;
  const vOffset = VERTICAL_OFFSET / zoom;

  // Find existing children to stack below them
  const existingChildren = getMindmapChildren(parentNode, elementsMap);

  let childX = parentNode.x + parentNode.width + hOffset;
  let childY: number;

  if (existingChildren.length === 0) {
    // Center child vertically with parent
    childY = parentNode.y + parentNode.height / 2 - childHeight / 2;
  } else {
    // Place below the lowest existing child
    let lowestY = -Infinity;
    for (const child of existingChildren) {
      const bottom = child.y + child.height;
      if (bottom > lowestY) {
        lowestY = bottom;
      }
    }
    childY = lowestY + vOffset;
  }

  // Determine branch color
  const parentOfParent = getMindmapParent(parentNode, elementsMap);
  let branchIndex: number;
  if (!parentOfParent) {
    // Parent is root — this child starts a new branch (or joins existing)
    branchIndex = existingChildren.length;
  } else {
    branchIndex = getMindmapBranchIndex(parentNode, elementsMap);
  }
  const branchColor = getMindmapBranchColor(branchIndex);

  const newNode = newElement({
    type: "rectangle",
    x: childX,
    y: childY,
    width: childWidth,
    height: childHeight,
    roundness: { type: 3 },
    roughness: parentNode.roughness,
    backgroundColor: branchColor.backgroundColor,
    strokeColor: branchColor.strokeColor,
    strokeWidth: parentNode.strokeWidth,
    opacity: parentNode.opacity,
    fillStyle: parentNode.fillStyle,
    strokeStyle: parentNode.strokeStyle,
    customData: {
      isMindmapNode: true,
      parentNodeId: parentNode.id,
      branchIndex,
    },
  });

  const bindingArrow = createMindmapBindingArrow(
    parentNode,
    newNode as unknown as ExcalidrawBindableElement,
    "right",
    scene,
    appState,
    branchColor.strokeColor,
  );

  return {
    newNode: newNode as OrderedExcalidrawElement,
    bindingArrow: bindingArrow as OrderedExcalidrawElement,
  };
};

/**
 * Create a sibling node below the selected node, connected to the same parent.
 */
export const addMindmapSibling = (
  selectedNode: ExcalidrawBindableElement,
  appState: AppState,
  scene: Scene,
): { newNode: OrderedExcalidrawElement; bindingArrow: OrderedExcalidrawElement } => {
  const elementsMap = scene.getNonDeletedElementsMap();
  const parentNode = getMindmapParent(selectedNode, elementsMap);

  // Scale new node dimensions and spacing by zoom for consistent visual size
  const zoom = appState.zoom?.value || 1;
  const sibWidth = NODE_WIDTH / zoom;
  const sibHeight = NODE_HEIGHT / zoom;
  const vOffset = VERTICAL_OFFSET / zoom;

  let siblingX: number;
  let siblingY: number;

  if (!parentNode) {
    // Root node: create sibling below with standard size
    siblingX = selectedNode.x;
    siblingY = selectedNode.y + selectedNode.height + vOffset;

    // New root sibling — gets its own branch color based on existing children count
    const rootChildren = getMindmapChildren(selectedNode, elementsMap);
    const branchIdx = rootChildren.length;
    const branchColor = getMindmapBranchColor(branchIdx);

    const newNode = newElement({
      type: "rectangle",
      x: siblingX,
      y: siblingY,
      width: sibWidth,
      height: sibHeight,
      roundness: { type: 3 },
      roughness: selectedNode.roughness,
      backgroundColor: branchColor.backgroundColor,
      strokeColor: branchColor.strokeColor,
      strokeWidth: selectedNode.strokeWidth,
      opacity: selectedNode.opacity,
      fillStyle: selectedNode.fillStyle,
      strokeStyle: selectedNode.strokeStyle,
      customData: {
        isMindmapNode: true,
        branchIndex: branchIdx,
      },
    });

    const bindingArrow = createMindmapBindingArrow(
      selectedNode,
      newNode as unknown as ExcalidrawBindableElement,
      "down",
      scene,
      appState,
      branchColor.strokeColor,
    );

    return {
      newNode: newNode as OrderedExcalidrawElement,
      bindingArrow: bindingArrow as OrderedExcalidrawElement,
    };
  }

  // Has parent: create new child of parent, below the lowest existing sibling
  siblingX = selectedNode.x;

  // Find the lowest sibling to place new one below it
  const siblings = getMindmapChildren(parentNode, elementsMap);
  let lowestBottom = selectedNode.y + selectedNode.height;
  for (const sibling of siblings) {
    const bottom = sibling.y + sibling.height;
    if (bottom > lowestBottom) {
      lowestBottom = bottom;
    }
  }
  siblingY = lowestBottom + vOffset;

  // Inherit branch color from selected node's branch
  const branchIndex = getMindmapBranchIndex(selectedNode, elementsMap);
  const siblingBranchColor = getMindmapBranchColor(branchIndex);

  const newNode = newElement({
    type: "rectangle",
    x: siblingX,
    y: siblingY,
    width: sibWidth,
    height: sibHeight,
    roundness: { type: 3 },
    roughness: selectedNode.roughness,
    backgroundColor: siblingBranchColor.backgroundColor,
    strokeColor: siblingBranchColor.strokeColor,
    strokeWidth: selectedNode.strokeWidth,
    opacity: selectedNode.opacity,
    fillStyle: selectedNode.fillStyle,
    strokeStyle: selectedNode.strokeStyle,
    customData: {
      isMindmapNode: true,
      parentNodeId: parentNode.id,
      branchIndex,
    },
  });

  const bindingArrow = createMindmapBindingArrow(
    parentNode as ExcalidrawBindableElement,
    newNode as unknown as ExcalidrawBindableElement,
    "right",
    scene,
    appState,
    siblingBranchColor.strokeColor,
  );

  return {
    newNode: newNode as OrderedExcalidrawElement,
    bindingArrow: bindingArrow as OrderedExcalidrawElement,
  };
};

/**
 * Create the initial root node of a mind map.
 */
export const createRootMindmapNode = (
  x: number,
  y: number,
  appState: AppState,
) => {
  return newElement({
    type: "rectangle",
    x,
    y,
    width: ROOT_NODE_WIDTH,
    height: ROOT_NODE_HEIGHT,
    roundness: { type: 3 },
    roughness: appState.currentItemRoughness,
    backgroundColor: appState.currentItemBackgroundColor,
    strokeColor: appState.currentItemStrokeColor,
    strokeWidth: appState.currentItemStrokeWidth,
    opacity: appState.currentItemOpacity,
    fillStyle: appState.currentItemFillStyle,
    strokeStyle: appState.currentItemStrokeStyle,
    customData: {
      isMindmapNode: true,
    },
  });
};

// ---------------------------------------------------------------------------
// Phase 4: Utility functions for branch colors, depth, and font size
// ---------------------------------------------------------------------------

/**
 * Get the depth of a mind map node (root = 0, its children = 1, etc.)
 */
export const getMindmapDepth = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): number => {
  let depth = 0;
  let current: ExcalidrawElement | null = node;
  while (current) {
    const parent = getMindmapParent(current, elementsMap);
    if (!parent) {
      break;
    }
    depth++;
    current = parent;
  }
  return depth;
};

/**
 * Get the branch index of a node (which direct child of root it belongs to).
 * Root's children sorted by Y position for deterministic indexing.
 * Returns -1 for the root node itself.
 */
export const getMindmapBranchIndex = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): number => {
  // Walk up to find root
  let current = node;
  let parent = getMindmapParent(current, elementsMap);

  if (!parent) {
    // This IS the root
    return -1;
  }

  // Walk up until parent is the root
  while (parent) {
    const grandparent = getMindmapParent(parent, elementsMap);
    if (!grandparent) {
      // parent is the root, current is the branch node
      break;
    }
    current = parent;
    parent = grandparent;
  }

  // parent is root, current is the direct child of root
  const rootChildren = getMindmapChildren(parent!, elementsMap)
    .slice()
    .sort((a, b) => a.y - b.y);

  return rootChildren.findIndex((child) => child.id === current.id);
};

/**
 * Get colors for a mind map branch based on its index.
 * Returns stroke (shade 3, medium-dark) and background (shade 1, light).
 */
export const getMindmapBranchColor = (
  branchIndex: number,
): { strokeColor: string; backgroundColor: string } => {
  const strokePalette = getAllColorsSpecificShade(3);
  const bgPalette = getAllColorsSpecificShade(1);
  const idx = Math.max(0, branchIndex) % strokePalette.length;
  return {
    strokeColor: strokePalette[idx],
    backgroundColor: bgPalette[idx],
  };
};

/**
 * Get the appropriate font size for a mind map node based on its depth.
 */
export const getMindmapFontSize = (depth: number): number => {
  return MINDMAP_FONT_SIZES[Math.min(depth, MINDMAP_FONT_SIZES.length - 1)];
};

/**
 * Reparent a mind map node to a new parent.
 * Removes old arrow, creates new arrow, updates customData, and re-layouts.
 */
export const reparentMindmapNode = (
  node: ExcalidrawElement,
  newParent: ExcalidrawBindableElement,
  scene: Scene,
  appState: AppState,
): void => {
  const elementsMap = scene.getNonDeletedElementsMap();
  const oldParent = getMindmapParent(node, elementsMap);

  if (!oldParent || oldParent.id === newParent.id) {
    return; // No-op if no parent or same parent
  }

  // Find the arrow connecting old parent → node
  const boundElements = node.boundElements ?? [];
  for (const bound of boundElements) {
    if (bound.type !== "arrow") {
      continue;
    }
    const arrow = elementsMap.get(bound.id) as ExcalidrawArrowElement | undefined;
    if (!arrow) {
      continue;
    }
    if (
      arrow.startBinding?.elementId === oldParent.id &&
      arrow.endBinding?.elementId === node.id
    ) {
      // Delete old arrow
      scene.mutateElement(arrow, { isDeleted: true });
      break;
    }
  }

  // Determine branch color from new parent
  const parentOfNewParent = getMindmapParent(newParent, elementsMap);
  let branchIndex: number;
  if (!parentOfNewParent) {
    // newParent is root — new branch
    const rootChildren = getMindmapChildren(newParent, elementsMap);
    branchIndex = rootChildren.length;
  } else {
    branchIndex = getMindmapBranchIndex(newParent, elementsMap);
  }
  const branchColor = getMindmapBranchColor(branchIndex);

  // Create new arrow from newParent → node
  createMindmapBindingArrow(
    newParent,
    node as unknown as ExcalidrawBindableElement,
    "right",
    scene,
    appState,
    branchColor.strokeColor,
  );

  // Update node customData
  scene.mutateElement(node, {
    customData: {
      ...node.customData,
      parentNodeId: newParent.id,
      branchIndex,
    },
    strokeColor: branchColor.strokeColor,
    backgroundColor: branchColor.backgroundColor,
  });

  // Recolor descendants in the moved sub-tree
  const recolorDescendants = (parent: ExcalidrawElement) => {
    const children = getMindmapChildren(parent, elementsMap);
    for (const child of children) {
      scene.mutateElement(child, {
        strokeColor: branchColor.strokeColor,
        backgroundColor: branchColor.backgroundColor,
        customData: { ...child.customData, branchIndex },
      });
      // Also recolor the arrow connecting parent → child
      for (const bound of child.boundElements ?? []) {
        if (bound.type !== "arrow") {
          continue;
        }
        const arrow = elementsMap.get(bound.id) as ExcalidrawArrowElement | undefined;
        if (arrow && arrow.startBinding?.elementId === parent.id) {
          scene.mutateElement(arrow, { strokeColor: branchColor.strokeColor });
        }
      }
      recolorDescendants(child);
    }
  };
  recolorDescendants(node);
};
